<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import IconInfo from '../../../icons/IconInfo.vue';
import Tooltip from './Tooltip.vue';

const props = defineProps({
  isVisible: Boolean,
  label: {
    type: String,
    default: ''
  },
  tooltip: {
    type: String,
    default: ''
  },
  theme: {
    type: String,
    default: 'secondary',
    validator: (value) => {
      return ['primary', 'secondary'].includes(value);
    },
  },
  xPosition: {
    type: String,
    default: 'right',
    validator: (value) => {
      return ['right', 'left', 'center'].includes(value);
    },
  },
  persistent: Boolean,
  custom: Boolean,
  title: {
    type: String,
    default: ''
  },
  text: {
    type: String,
    default: ''
  },
  distance: {
    type: Number,
    default: 4
  }
});
const emit = defineEmits(['close']);

const tooltipContainer = ref(null);
const isTooltipVisible = ref(false);
const targetLeft = ref(false);
const tooltipEl = ref(null);

const handleTooltipBlur = async (e) => {
  if (!tooltipEl.value.tooltip.contains(e.target)) {
    targetLeft.value = true;
  }
};

const handleTooltipMouseEnter = async (e) => {
  if (props.persistent) return;
  if (tooltipContainer.value !== e.target) {
    isTooltipVisible.value = false;
  }
  await nextTick();
  isTooltipVisible.value = true;
  targetLeft.value = false;
};

const handleTooltipClose = () => {
  isTooltipVisible.value = false
  targetLeft.value = false;
  emit('close');
};

const handleInfoClick = () => {
  if (!props.persistent) return;
  if (isTooltipVisible.value) {
    targetLeft.value = true;
  } else {
    isTooltipVisible.value = true;
    targetLeft.value = false;
  }
};

watch(isTooltipVisible, (newValue) => {
  if (props.persistent) {
    if (newValue) {
      window.addEventListener('click', handleTooltipBlur, true);
    } else {
      window.removeEventListener('click', handleTooltipBlur, true);
    }
  }
});

onMounted(() => {
  if (props.isVisible) {
    isTooltipVisible.value = true;
  }
});

watch(() => props.isVisible, (newValue) => {
  isTooltipVisible.value = newValue;
});

defineExpose({
  closeTooltip: () => { 
    targetLeft.value = true;
  }
});
</script>

<template>
  <div class="tooltip">
    <span v-if="label" class="tooltip__label">
      {{ label }}
    </span>
    <span
      ref="tooltipContainer"
      role="button"
      :aria-label="isTooltipVisible ? 'Hide tooltip' : 'Show tooltip'"
      class="tooltip__icon"
      @mouseenter="handleTooltipMouseEnter"
      @mouseleave="persistent ? undefined : targetLeft = true"
      @click="handleInfoClick"
    >
      <icon-info color="#49454F" size="16" />
    </span>
  </div>

  <tooltip
    v-if="isTooltipVisible"
    ref="tooltipEl"
    :target-left="targetLeft"
    :text="custom ? '' : tooltip"
    :theme="theme"
    :x-position="xPosition"
    :custom="custom"
    :persistent="persistent"
    :distance="distance"
    :rect="tooltipContainer?.getBoundingClientRect()"
    @close="handleTooltipClose"
  >
    <slot v-if="custom && !title && !text" />
    <div v-else-if="custom && title && text" class="pge-tooltip__body">
      <h4>{{ title }}</h4>
      <p>{{ text }}</p>
      <div class="pge-tooltip__body__actions" v-if="$slots.actions">
        <slot name="actions" />
      </div>
    </div>
  </tooltip>
</template>

<style lang="scss" scoped>
.tooltip {
  display: flex;
  align-items: center;
  column-gap: 4px;

  &__label {
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    color: #49454F;
    cursor: default;
  }

  &__icon {
    display: flex;
    padding: 4px;
    border-radius: 100%;
    cursor: help;
  }

  &__icon:hover {
    background-color: #5E2F7814;
  }
}

.pge-tooltip__body {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  padding: 6px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    margin: 0;
    color: #49454F;
  }

  p {
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    color: #4B444D;
  }

  &__actions {
    display: flex;
    align-items: center;
    column-gap: 8px;
    margin-top: 8px;
  }
}
</style>
